/**
 * AI Assistant — the dock (launcher + panel state machine).
 *
 * Mounted once in `EmployerLayout` (G1), so it exists on every employer page.
 * The scope ("All jobs" or one job) is derived from the route unless the HR
 * pinned one from the header switcher (see scope.js); every request carries
 * `scope` plus `ui_context.page` / `ui_context.scope`.
 *
 * Renders nothing until GET /api/copilot/status returns enabled:true (cached
 * per scope in module scope). The one exception is a plan gate: when status
 * (or a chat call) answers `upgrade_required`, the launcher is shown and the
 * panel body renders the "Copilot isn't in your plan" card instead of a chat.
 *
 * Each employer page renders its own <EmployerLayout>, so this component
 * remounts on route changes; open/minimised state, the active thread per
 * scope and the last scope are persisted in localStorage so the dock feels
 * continuous across navigation. A scope change starts a fresh thread with a
 * one-line system note ("Switched to All jobs").
 *
 * Owns conversation state, streaming, voice toggles, keyboard shortcuts,
 * rate-limit countdown and persistence; layout lives in CopilotPanel.jsx /
 * CopilotComposer.jsx. Visual system: copilot.css.
 */
import './copilot.css';
import CopilotAvatar from './CopilotAvatar';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip';
import { cn } from '@/lib/utils';
import { compactFilters, useCopilot } from './CopilotContext';
import { fetchConversation, fetchStatus, fetchSuggestions } from './copilotApi';
import { useCopilotStream } from './useCopilotStream';
import copilotTts from './copilotTts';
import CopilotPanel from './CopilotPanel';
import { conversationScope } from './CopilotHistory';
import {
  COMPANY_LABEL,
  pageLabel,
  readPinnedScope,
  resolveScope,
  routeJobId,
  scopeKey,
  scopeLabel,
  scopesEqual,
  writePinnedScope,
} from './scope';

// ---------------------------------------------------------------------------
// status cache (per scope, module scope)
// ---------------------------------------------------------------------------
const statusCache = new Map();

export function __resetCopilotStatusCache() {
  statusCache.clear();
}

function loadStatus(scope) {
  const key = scopeKey(scope);
  if (!statusCache.has(key)) {
    const promise = fetchStatus(scope)
      .then((data) => {
        if (data && data.enabled) return data;
        return { enabled: false, upgradeRequired: Boolean(data?.upgrade_required) };
      })
      .catch((err) => {
        const upgradeRequired = Boolean(err?.response?.data?.upgrade_required);
        if (!upgradeRequired) statusCache.delete(key); // allow a retry on next mount
        return { enabled: false, upgradeRequired };
      });
    statusCache.set(key, promise);
  }
  return statusCache.get(key);
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const FILTER_LABELS = {
  status: 'status',
  skills: 'skills',
  location: 'location',
  experienceMin: 'min exp',
  experienceMax: 'max exp',
  resumeScoreMin: 'min score',
  resumeScoreMax: 'max score',
  minScore: 'min score',
  maxScore: 'max score',
  testCompleted: 'tests',
  interviewFilter: 'interview',
  uploaderFilter: 'uploaded by',
  collegeTierFilter: 'college',
  appliedDateFilter: 'applied',
  relevanceFilter: 'fit',
};

/** Active filters as read-only chip descriptors. */
function filterChips(filters) {
  const active = compactFilters(filters);
  return Object.entries(active)
    .filter(([k]) => FILTER_LABELS[k])
    .map(([k, v]) => ({ key: k, label: `${FILTER_LABELS[k]} ${String(v).replace(/_/g, ' ')}` }));
}

function storage(key, value) {
  try {
    if (value === undefined) return window.localStorage.getItem(key);
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch {
    // storage unavailable
  }
  return undefined;
}

const MODE_KEY = 'copilot.mode';
const LAST_SCOPE_KEY = 'copilot.lastScope';
const convKey = (key) => `copilot.conv.${key}`;

let keyCounter = 0;
const nextKey = (prefix) => `${prefix}-${Date.now()}-${(keyCounter += 1)}`;

function historyToMessage(m) {
  return {
    key: `h-${m.id}`,
    id: m.id,
    role: m.role,
    content: m.content || '',
    blocks: Array.isArray(m.blocks) ? m.blocks : [],
    citations: Array.isArray(m.citations) ? m.citations : [],
    speech: m.speech || null,
    toolTrace: Array.isArray(m.tool_trace) ? m.tool_trace : [],
    rating: m.rating ?? null,
    created_at: m.created_at,
    fromHistory: true,
  };
}

function systemNote(text) {
  return { key: nextKey('s'), role: 'assistant', content: text, system: true };
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 767px)').matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setMobile(Boolean(mq.matches));
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return mobile;
}

function isTypingTarget(el) {
  if (!el) return false;
  const tag = String(el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

/** Scope-aware `ui_context` (page + scope on top of what the page published). */
export function buildUiContext(pageContext, page, scope) {
  const ctx = { ...(pageContext || {}), page };
  ctx.scope = scope?.type === 'job' ? { type: 'job', job_id: scope.job_id } : { type: 'company' };
  return ctx;
}

// ---------------------------------------------------------------------------
// component
// ---------------------------------------------------------------------------
export default function ApplicationsCopilot() {
  const copilot = useCopilot();
  const { page, activePage, location, jobId: pageJobId, jobDetails, candidates, filters, selectedCandidate, isOpen, open, close, uiContext } = copilot;

  // ---- scope: route-derived unless pinned ----------------------------------
  const [pin, setPin] = useState(() => readPinnedScope());
  const resolved = useMemo(() => resolveScope(location, pin), [location, pin]);
  useEffect(() => {
    if (resolved.pinStale) {
      writePinnedScope(null);
      setPin(null);
    }
  }, [resolved.pinStale]);
  const scope = resolved.scope;
  const key = scopeKey(scope);
  const jobScoped = scope.type === 'job';

  const [status, setStatus] = useState(null);
  const [scopes, setScopes] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mode, setMode] = useState(() => {
    const saved = storage(MODE_KEY);
    return saved === 'docked' || saved === 'expanded' || saved === 'minimised' ? saved : 'docked';
  });
  const [view, setView] = useState('chat');
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [input, setInput] = useState('');
  const [lastUserMessage, setLastUserMessage] = useState('');
  const [retryIn, setRetryIn] = useState(0); // seconds left on a 429 countdown
  const [chatGate, setChatGate] = useState(false); // chat answered upgrade_required
  const [hadNetworkError, setHadNetworkError] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(() => copilotTts.isEnabled());
  const [ttsSpeaking, setTtsSpeaking] = useState(false);
  const [speakingKey, setSpeakingKey] = useState(null);
  const inputRef = useRef(null);
  const isMobile = useIsMobile();
  const speechSeenRef = useRef(false);
  const ttsRef = useRef(ttsEnabled);
  ttsRef.current = ttsEnabled;
  const prevKeyRef = useRef(null);
  const pendingConvRef = useRef(null); // thread picked from history in another scope

  // ---- status gate (per scope) ---------------------------------------------
  useEffect(() => {
    if (copilot.isDefault) return undefined;
    let cancelled = false;
    loadStatus(scope).then((s) => {
      if (cancelled) return;
      setStatus(s);
      if (s?.scopes) setScopes(s.scopes);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, copilot.isDefault]);

  const enabled = Boolean(status?.enabled);
  const planGated = Boolean(status?.upgradeRequired) || chatGate;
  // The dock is visible when the feature is on, or when it is plan-gated (so
  // the user can discover it and see the upgrade card).
  const visible = enabled || Boolean(status?.upgradeRequired);
  const voiceEnabled = enabled && status?.voice_enabled !== false;
  const maxChars = Number(status?.limits?.max_message_chars) || 2000;

  // ---- persisted mode -------------------------------------------------------
  useEffect(() => {
    if (visible) storage(MODE_KEY, mode);
  }, [visible, mode]);

  // ---- scope switch → fresh thread (restore the thread on a same-scope remount)
  const scopeTitle = useMemo(() => {
    if (!jobScoped) return COMPANY_LABEL;
    const fromStatus = status?.job && String(status.job.job_id) === String(scope.job_id) ? status.job.job_title : null;
    const fromPage = String(pageJobId) === String(scope.job_id) ? jobDetails?.job_title : null;
    return scopeLabel(scope, scopes?.jobs, fromStatus || fromPage || pin?.title || 'this job');
  }, [jobScoped, scope, status, pageJobId, jobDetails, scopes, pin]);

  useEffect(() => {
    if (!visible) return;
    const lastKey = storage(LAST_SCOPE_KEY);
    const pendingConv = pendingConvRef.current;
    pendingConvRef.current = null;
    if (prevKeyRef.current === null) {
      // Mount (page load or route remount).
      const saved = Number(storage(convKey(key)));
      if (pendingConv) {
        setConversationId(pendingConv);
        setMessages([]);
      } else if (lastKey === key && Number.isFinite(saved) && saved > 0) {
        setConversationId(saved);
      } else {
        setConversationId(null);
        setMessages(lastKey && lastKey !== key ? [systemNote(`Switched to ${scopeTitle}`)] : []);
      }
    } else if (prevKeyRef.current !== key) {
      // In-session switch from the header chip / history drawer.
      copilotTts.stop();
      setInput('');
      copilot.clearSelection();
      if (pendingConv) {
        setConversationId(pendingConv);
        setMessages([]);
      } else {
        setConversationId(null);
        setMessages([systemNote(`Switched to ${scopeTitle}`)]);
      }
    }
    prevKeyRef.current = key;
    storage(LAST_SCOPE_KEY, key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, key]);

  useEffect(() => {
    if (enabled) storage(convKey(key), conversationId || null);
  }, [enabled, key, conversationId]);

  // ---- suggestions + unread dot (per scope) ----------------------------------
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetchSuggestions(scope)
      .then((list) => {
        if (cancelled) return;
        setSuggestions(list);
        if (list.length && !isOpen) setUnreadCount(list.length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  useEffect(() => {
    if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  // ---- TTS subscription -------------------------------------------------------
  useEffect(
    () =>
      copilotTts.subscribe(({ speaking, enabled: on }) => {
        setTtsEnabled(on);
        setTtsSpeaking(Boolean(speaking));
        if (!speaking) setSpeakingKey(null);
      }),
    []
  );

  // ---- streaming ----------------------------------------------------------------
  const onStreamEvent = useCallback((event, localState) => {
    if (event.type === 'speech' && event.text) {
      speechSeenRef.current = true;
      if (ttsRef.current) void copilotTts.speak(event.text);
    }
    if (event.type === 'done' && !speechSeenRef.current && ttsRef.current && localState?.content) {
      void copilotTts.speak(localState.content);
    }
  }, []);

  const onStreamFinish = useCallback((snapshot) => {
    if (snapshot.conversationId) setConversationId(snapshot.conversationId);
    setMessages((prev) => [
      ...prev,
      {
        key: nextKey('a'),
        id: snapshot.id,
        role: 'assistant',
        content: snapshot.content,
        blocks: snapshot.blocks,
        citations: snapshot.citations,
        speech: snapshot.speech,
        statusLines: snapshot.statusLines,
        toolTrace: snapshot.toolTrace,
        latencyMs: snapshot.latencyMs,
        error: snapshot.error,
        cancelled: snapshot.cancelled,
      },
    ]);
    if (snapshot.error) {
      const { status: httpStatus, retryAfter, upgradeRequired, message, code } = snapshot.error;
      setHadNetworkError(code === 'network' || code === 'incomplete');
      if (upgradeRequired) {
        setChatGate(true);
        toast.error('The AI Assistant is not included in your plan.');
      } else if (httpStatus === 409) {
        toast.info('Another answer is still streaming');
      } else if (httpStatus === 401 || httpStatus === 403 || httpStatus === 503) {
        toast.error(message);
      }
      if (retryAfter) setRetryIn(Math.max(1, Math.ceil(Number(retryAfter))));
    } else {
      setHadNetworkError(false);
    }
  }, []);

  const stream = useCopilotStream({ onEvent: onStreamEvent, onFinish: onStreamFinish });
  const inFlight = stream.state.active && stream.state.isStreaming ? { ...stream.state, key: 'inflight', role: 'assistant' } : null;

  // Reset the in-flight slot once committed.
  useEffect(() => {
    if (stream.state.active && !stream.state.isStreaming) stream.reset();
  }, [stream.state.active, stream.state.isStreaming, stream]);

  // Cancel a running answer when the scope changes under it.
  useEffect(() => {
    if (stream.state.isStreaming) stream.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Rate-limit countdown (seconds, from the 429 `retry_after`).
  useEffect(() => {
    if (retryIn <= 0) return undefined;
    const t = window.setTimeout(() => setRetryIn((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [retryIn]);

  const fullUiContext = useMemo(() => buildUiContext(uiContext, page, scope), [uiContext, page, scope]);

  const sendMessage = useCallback(
    (text, { voice = false } = {}) => {
      const message = String(text || '').trim();
      if (!message || stream.state.isStreaming || retryIn > 0 || planGated) return;
      copilotTts.stop();
      speechSeenRef.current = false;
      setMessages((prev) => [...prev, { key: nextKey('u'), role: 'user', content: message }]);
      setLastUserMessage(message);
      setInput('');
      void stream.send({ scope, message: message.slice(0, maxChars), conversationId, voice, uiContext: fullUiContext });
    },
    [scope, stream, conversationId, fullUiContext, maxChars, retryIn, planGated]
  );

  const handlePrompt = useCallback(
    (prompt, opts = {}) => {
      if (opts.system) {
        // Local note from a confirmed action (e.g. a scheduled test); no model call.
        setMessages((prev) => [...prev, systemNote(prompt)]);
        return;
      }
      if (opts.prefill) {
        setInput(prompt);
        window.setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
      sendMessage(prompt);
    },
    [sendMessage]
  );

  const handleRetry = useCallback(
    (message) => {
      setMessages((prev) => prev.filter((m) => m.key !== message.key));
      setRetryIn(0);
      void stream.retry();
    },
    [stream]
  );

  // ---- pending prompt from askAbout() ----------------------------------------
  useEffect(() => {
    if (!copilot.pendingPrompt || !enabled) return;
    const pending = copilot.consumePendingPrompt();
    if (!pending) return;
    setMode((m) => (m === 'minimised' ? 'docked' : m));
    setView('chat');
    if (pending.prompt) sendMessage(pending.prompt);
    else {
      setInput(`About ${pending.name}: `);
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [copilot.pendingPrompt, copilot, enabled, sendMessage]);

  // ---- load persisted conversation when opened --------------------------------
  useEffect(() => {
    if (!enabled || !isOpen || !conversationId || messages.length || loadingHistory) return undefined;
    let cancelled = false;
    setLoadingHistory(true);
    fetchConversation(conversationId)
      .then(({ messages: list }) => {
        if (!cancelled) setMessages(list.map(historyToMessage));
      })
      .catch(() => {
        if (!cancelled) setConversationId(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isOpen, conversationId]);

  const newChat = useCallback(() => {
    if (stream.state.isStreaming) stream.cancel();
    copilotTts.stop();
    setConversationId(null);
    setMessages([]);
    setInput('');
    setView('chat');
    copilot.clearSelection();
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [stream, copilot]);

  // ---- scope switcher ------------------------------------------------------------
  const pickScope = useCallback(
    (nextScope, title) => {
      const fromRoute = copilot.routeScope;
      if (scopesEqual(nextScope, fromRoute)) {
        // Back to what the route implies: no pin needed.
        writePinnedScope(null);
        setPin(null);
        return;
      }
      writePinnedScope({ scope: nextScope, routeJobId: routeJobId(location), title: title || null });
      setPin(readPinnedScope());
    },
    [copilot.routeScope, location]
  );

  const selectConversation = useCallback(
    (conv) => {
      const id = typeof conv === 'object' && conv !== null ? conv.id : conv;
      if (!id) return;
      if (stream.state.isStreaming) stream.cancel();
      setView('chat');
      const convScope = typeof conv === 'object' && conv !== null ? conversationScope(conv, scope) : scope;
      if (!scopesEqual(convScope, scope)) {
        // Switch the scope chip first; the scope effect then adopts this thread.
        pendingConvRef.current = id;
        pickScope(convScope, conv.job_title || null);
        return;
      }
      setMessages([]);
      setConversationId(id);
    },
    [stream, scope, pickScope]
  );

  // ---- keyboard: "/" focus, Esc close ------------------------------------------
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        open();
        setMode((m) => (m === 'minimised' ? 'docked' : m));
        window.setTimeout(() => inputRef.current?.focus(), 50);
      } else if (e.key === 'Escape' && isOpen) {
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, isOpen, open, close]);

  // Focus the input when the panel opens.
  useEffect(() => {
    if (isOpen && mode !== 'minimised' && view === 'chat') window.setTimeout(() => inputRef.current?.focus(), 80);
  }, [isOpen, mode, view]);

  // Stop audio when closed / unmounted.
  useEffect(() => {
    if (!isOpen) copilotTts.stop();
  }, [isOpen]);
  useEffect(() => () => copilotTts.stop(), []);

  // ---- derived header context ------------------------------------------------
  const onScopedJobPage = jobScoped && activePage?.page === 'applications' && String(pageJobId) === String(scope.job_id);
  const context = useMemo(() => {
    const count = jobScoped
      ? status?.job && String(status.job.job_id) === String(scope.job_id)
        ? status.job.applicant_count
        : onScopedJobPage
          ? candidates?.length ?? 0
          : null
      : null;
    return {
      page,
      pageLabel: pageLabel(page),
      title: scopeTitle,
      count,
      filters: onScopedJobPage ? filterChips(filters) : [],
    };
  }, [jobScoped, status, scope, onScopedJobPage, candidates, page, scopeTitle, filters]);

  const currentJob = useMemo(() => {
    if (!jobScoped) return null;
    const fromStatus = status?.job && String(status.job.job_id) === String(scope.job_id) ? status.job : null;
    return {
      job_id: scope.job_id,
      title: fromStatus?.job_title || scopeTitle,
      applicant_count: fromStatus?.applicant_count ?? (onScopedJobPage ? candidates?.length : undefined),
    };
  }, [jobScoped, status, scope, scopeTitle, onScopedJobPage, candidates]);

  const selectedName = selectedCandidate ? selectedCandidate.name || selectedCandidate.candidate_name || null : null;


  if (!visible || copilot.isDefault) return null;

  const showPanel = isOpen && mode !== 'minimised';
  const isStreaming = stream.state.isStreaming;
  const streamingHidden = isStreaming && !showPanel;
  const showUnread = unreadCount > 0 && !isOpen;
  // On Messages the launcher moves left of the composer's send button.
  const shiftLeft = page === 'messages';

  const toggleDock = () => {
    if (isOpen && mode !== 'minimised') close();
    else {
      setMode((m) => (m === 'minimised' ? 'docked' : m));
      open();
    }
  };

  return (
    <>
      {showPanel && (
        <CopilotPanel
          isMobile={isMobile}
          mode={mode}
          onSetMode={setMode}
          onMinimise={() => setMode('minimised')}
          onClose={close}
          context={context}
          selectedName={selectedName}
          onClearSelection={copilot.clearSelection}
          view={view}
          onShowHistory={() => setView('history')}
          onBackToChat={() => setView('chat')}
          onNewChat={newChat}
          scope={scope}
          scopes={scopes}
          currentJob={currentJob}
          onPickScope={pickScope}
          activeConversationId={conversationId}
          onSelectConversation={selectConversation}
          onDeletedConversation={(id) => {
            if (id === conversationId) newChat();
          }}
          messages={messages}
          inFlight={inFlight}
          loadingHistory={loadingHistory}
          suggestions={suggestions}
          onPrompt={handlePrompt}
          onRetry={handleRetry}
          onSpeak={(text, msgKey) => {
            setSpeakingKey(msgKey || 'replay');
            void copilotTts.speak(text, { force: true });
          }}
          onStopSpeak={() => copilotTts.stop()}
          speakingKey={speakingKey}
          isStreaming={isStreaming}
          hadNetworkError={hadNetworkError}
          planGated={planGated}
          composer={{
            inputRef,
            value: input,
            onChange: (next) => setInput(next),
            onSend: ({ voice } = {}) => sendMessage(input, { voice }),
            onStop: () => stream.cancel(),
            isStreaming,
            disabled: planGated,
            maxChars,
            lastUserMessage,
            voiceEnabled,
            ttsEnabled,
            ttsSpeaking,
            onToggleTts: () => copilotTts.setEnabled(!copilotTts.isEnabled()),
            retryIn,
          }}
        />
      )}

      {/* Minimised = just the launcher orb (it breathes while an answer streams; a click restores). */}
      {!(isMobile && showPanel) && (
        <div
          className={cn('cp-launcher-wrap fixed bottom-6 z-50', shiftLeft ? 'right-[calc(1.5rem+72px)]' : 'right-6')}
          data-testid="copilot-launcher-wrap"
        >
          {!isMobile && (
            <span className="cp-launcher-label cp-glass-strong" aria-hidden="true" data-testid="copilot-launcher-label">
              Ask AI Assistant
            </span>
          )}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleDock}
                  aria-label="Ask AI Assistant (/)"
                  aria-expanded={isOpen}
                  data-testid="copilot-launcher"
                  className={cn('cp-orb cp-orb-ring cp-launcher-orb h-[52px] w-[52px] shrink-0', streamingHidden && 'cp-orb-breathe')}
                >
                  <CopilotAvatar size={52} className="cp-launcher-face" title="" aria-hidden="true" />
                  {showUnread && (
                    <span className="cp-launcher-dot cp-num" data-testid="copilot-unread" aria-hidden="true">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-[11px]">
                {showUnread ? `${unreadCount} new ${unreadCount === 1 ? 'insight' : 'insights'}` : 'Ask AI Assistant (/)'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </>
  );
}
